/**
 * Shared configurable SettingsModal component
 * Consolidates 28+ duplicate SettingsModal implementations
 */
import { FC, useState } from 'react';

import { CloseButton } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, RadioCircle, RadioInput } from '@/components/Forms';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';
import Text from '@/components/Text';

import {
  ActionBox,
  ActionTitle,
  Dialog,
  ModalContent,
  ModalHeader,
  StyledRadiosLabel,
} from './styledComponents';

export interface SettingOption {
  label: string;
  value: string;
}

export interface SettingSectionConfig {
  /** Section title */
  title: string;
  /** Type of control */
  type: 'radio' | 'checkbox' | 'input';
  /** State key in ImageData */
  stateKey: string;
  /** Available options (for radio) */
  options?: SettingOption[] | string[];
  /** Checkbox labels (for checkboxes) */
  checkboxes?: Array<{ key: string; label: string }>;
  /** Input placeholder (for input) */
  placeholder?: string;
  /** Input type (for input) */
  inputType?: 'text' | 'number';
  /** Show condition */
  show?: boolean;
  /** Lay the options out in a grid of N columns instead of a single row */
  columns?: number;
}

interface Props<T = any> {
  /** Image data object */
  image: T;
  /** Index in images array */
  imageIndex: number;
  /** Close callback */
  onClose: () => void;
  /** Update images callback */
  setImages: React.Dispatch<React.SetStateAction<T[]>>;
  /** Modal title */
  title?: string;
  /** Configuration for each settings section */
  sections: SettingSectionConfig[];
}

/**
 * Generic Settings Modal Component
 *
 * @example
 * // Simple usage with divider only
 * <SettingsModal
 *   image={image}
 *   imageIndex={0}
 *   onClose={handleClose}
 *   setImages={setImages}
 *   title="Image Parameters"
 *   sections={[
 *     {
 *       title: 'Cover Divider',
 *       type: 'radio',
 *       stateKey: 'divider',
 *       options: ['none', 'white', 'black']
 *     }
 *   ]}
 * />
 *
 * @example
 * // Complex usage with multiple settings
 * <SettingsModal
 *   image={image}
 *   imageIndex={0}
 *   onClose={handleClose}
 *   setImages={setImages}
 *   title="Left Image Parameters"
 *   sections={[
 *     {
 *       title: 'Not The Actual Photo',
 *       type: 'radio',
 *       stateKey: 'actualPhoto',
 *       options: ['none', 'bottom', 'top']
 *     },
 *     {
 *       title: 'Text Position',
 *       type: 'radio',
 *       stateKey: 'textPosition',
 *       options: ['bottom', 'top']
 *     },
 *     {
 *       title: 'Text Parameters',
 *       type: 'checkbox',
 *       stateKey: '',
 *       checkboxes: [
 *         { key: 'quotes', label: 'Quotes' },
 *         { key: 'brakes', label: 'Custom brakes' }
 *       ]
 *     }
 *   ]}
 * />
 */
const SettingsModal: FC<Props> = ({
  onClose,
  image,
  setImages,
  imageIndex,
  title = 'Image Parameters',
  sections,
}) => {
  // Create state for each setting dynamically
  const [settings, setSettings] = useState<Record<string, any>>(() => {
    const initialState: Record<string, any> = {};
    sections.forEach((section) => {
      if (section.type === 'radio' || section.type === 'input') {
        initialState[section.stateKey] = image[section.stateKey];
      } else if (section.type === 'checkbox' && section.checkboxes) {
        section.checkboxes.forEach((cb) => {
          initialState[cb.key] = image[cb.key];
        });
      }
    });
    return initialState;
  });

  const onUpdateSettings = () => {
    setImages((prev) => {
      const newArray = [...prev];
      newArray[imageIndex] = {
        ...image,
        ...settings,
      };
      return newArray;
    });
    onClose();
  };

  const updateSetting = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCheckbox = (key: string) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <Text $type="button">{title}</Text>
          <CloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          {sections.map((section, idx) => {
            // Skip if show condition is false
            if (section.show === false) return null;

            if (section.type === 'radio' && section.options) {
              const options =
                typeof section.options[0] === 'string'
                  ? (section.options as string[]).map((opt) => ({
                      label: opt,
                      value: opt,
                    }))
                  : (section.options as SettingOption[]);

              return (
                <div key={idx}>
                  <ActionTitle $type="smallHeadline">{section.title}</ActionTitle>
                  <ActionBox $columns={section.columns}>
                    {options.map((option) => (
                      <StyledRadiosLabel key={option.value}>
                        {option.label}
                        <RadioInput
                          type="radio"
                          name={section.stateKey}
                          checked={settings[section.stateKey] === option.value}
                          onChange={() => updateSetting(section.stateKey, option.value)}
                        />
                        <RadioCircle />
                      </StyledRadiosLabel>
                    ))}
                  </ActionBox>
                </div>
              );
            }

            if (section.type === 'checkbox' && section.checkboxes) {
              return (
                <div key={idx}>
                  <ActionTitle $type="smallHeadline">{section.title}</ActionTitle>
                  <ActionBox>
                    {section.checkboxes.map((checkbox) => (
                      <Checkbox
                        key={checkbox.key}
                        checked={settings[checkbox.key]}
                        onChange={() => toggleCheckbox(checkbox.key)}
                      >
                        {checkbox.label}
                      </Checkbox>
                    ))}
                  </ActionBox>
                </div>
              );
            }

            if (section.type === 'input') {
              return (
                <div key={idx}>
                  <ActionTitle $type="smallHeadline">{section.title}</ActionTitle>
                  <Input
                    value={settings[section.stateKey] || ''}
                    onChange={(e) => {
                      const value = section.inputType === 'number'
                        ? parseInt(e.target.value) || undefined
                        : e.target.value;
                      updateSetting(section.stateKey, value);
                    }}
                    placeholder={section.placeholder || ''}
                    type={section.inputType || 'text'}
                  />
                </div>
              );
            }

            return null;
          })}
        </ModalContent>
        <SidePanelFooter $actions>
          <Button type="button" onClick={onUpdateSettings}>
            Apply
          </Button>
          <Button color="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
        </SidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default SettingsModal;
